import { useEffect, useState } from "react";
import DashboardShell from "../components/DashboardShell";
import { api, formatApiError } from "../lib/api";
import { Copy, Link as LinkIcon, Network, Share2 } from "lucide-react";

export default function ManageOrg() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api
      .get("/referrals/mine")
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, []);

  const inviteLink = data
    ? `${window.location.origin}/?ref=${data.ref_code}`
    : "";

  const copy = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      console.warn("[invite] clipboard write blocked:", err?.message);
    }
  };

  return (
    <DashboardShell>
      <div className="space-y-8" data-testid="manage-org-page">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#00aaff]">// Manage Organization</div>
          <h1 className="text-3xl lg:text-4xl font-semibold mt-1">
            {data?.organization_name || "Your Organization"}
          </h1>
          <p className="text-[#94a3b8] mt-2 max-w-xl">
            Invite peer charities and supporters. Every signup using your link is tracked and credited to your organization.
          </p>
        </div>

        {error && (
          <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded px-4 py-3" data-testid="manage-org-error">
            {error}
          </div>
        )}

        {data && (
          <>
            <section className="csc-card p-6 lg:p-8" data-testid="invite-card">
              <div className="flex items-center gap-2 mb-3">
                <Share2 size={16} className="text-[#39ff14]" />
                <h2 className="font-semibold">Your Invite Link</h2>
              </div>
              <p className="text-sm text-[#94a3b8] mb-4">
                Share this link with peer nonprofits. They&apos;ll see your name on the registration page and get attributed to you automatically.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch gap-3">
                <div className="flex-1 flex items-center gap-3 px-4 py-3 rounded-md border border-[rgba(0,170,255,0.18)] bg-[#050d1a] font-mono-tech text-sm">
                  <LinkIcon size={14} className="text-[#00aaff] shrink-0" />
                  <span className="text-white break-all" data-testid="invite-link-value">{inviteLink}</span>
                </div>
                <button
                  onClick={copy}
                  className={copied ? "csc-btn-secondary" : "csc-btn-primary"}
                  data-testid="invite-copy-btn"
                >
                  <Copy size={14} />
                  {copied ? "Copied!" : "Copy Link"}
                </button>
              </div>

              <div className="mt-4 font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#94a3b8]">
                Ref Code: <span className="text-[#00aaff]">{data.ref_code}</span>
              </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="invite-stats">
              {[
                { label: "Total Conversions", value: data.stats.total_conversions, color: "#39ff14" },
                { label: "People Referred", value: data.stats.people_conversions, color: "#00aaff" },
                { label: "Orgs Referred", value: data.stats.org_conversions, color: "#39ff14" },
              ].map((s) => (
                <div key={s.label} className="csc-card p-5">
                  <div className="flex items-center justify-between">
                    <Network size={16} style={{ color: s.color }} />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">Lifetime</span>
                  </div>
                  <div className="mt-4 text-3xl font-semibold">{s.value}</div>
                  <div className="text-xs text-[#94a3b8] mt-1">{s.label}</div>
                </div>
              ))}
            </section>

            <p className="text-xs text-[#94a3b8] font-mono-tech">
              Full organization profile editor, members management, and security posture tools launch in Phase 2.
            </p>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
